/* ==========================================================================
   ORYX TOKYO NIGHT — global theme JS
   Cart drawer, quick view, predictive search, wishlist, recently viewed,
   3D tilt, scroll reveal, countdowns, toasts. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  // Signals that JS is alive; CSS drops its "show everything anyway" fallback.
  document.documentElement.classList.add('js-ready');

  const T = window.theme || {};
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ---------- Utils ---------- */

  function formatMoney(paise) {
    const amount = (paise / 100).toLocaleString('en-IN', {
      minimumFractionDigits: paise % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
    const fmt = T.moneyFormat || '₹{{amount}}';
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async function fetchJSON(url, opts) {
    const res = await fetch(url, opts);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return res.json();
  }

  function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------- Toast ---------- */

  const toastRoot = $('#toast-root');
  function toast(msg, ms = 2600) {
    if (!toastRoot) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = msg;
    toastRoot.appendChild(el);
    setTimeout(() => {
      el.classList.add('leaving');
      el.addEventListener('animationend', () => el.remove(), { once: true });
    }, ms);
  }
  window.oryxToast = toast;

  /* ---------- Overlay + drawers ---------- */

  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);

  let openDrawer = null;
  function drawerOpen(el) {
    if (openDrawer) openDrawer.classList.remove('open');
    openDrawer = el;
    el.classList.add('open');
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    const focusable = el.querySelector('button, a, input');
    if (focusable) focusable.focus({ preventScroll: true });
  }
  function drawerCloseAll() {
    if (openDrawer) openDrawer.classList.remove('open');
    openDrawer = null;
    overlay.classList.remove('open');
    $$('.search-overlay.open').forEach((s) => s.classList.remove('open'));
    closeModal();
    document.body.style.overflow = '';
  }
  overlay.addEventListener('click', drawerCloseAll);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') drawerCloseAll(); });
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); drawerCloseAll(); }
  });

  /* ---------- Modal ---------- */

  let modalEl = null;
  function openModal(innerHTML, small) {
    closeModal();
    modalEl = document.createElement('div');
    modalEl.className = 'modal';
    modalEl.innerHTML =
      '<div class="overlay open" data-modal-overlay style="position:absolute"></div>' +
      '<div class="modal__panel' + (small ? ' modal__panel--sm' : '') + '" role="dialog" aria-modal="true">' +
      '<button class="modal__close" data-modal-close aria-label="Close">' + iconSvg('close') + '</button>' +
      innerHTML + '</div>';
    document.body.appendChild(modalEl);
    document.body.style.overflow = 'hidden';
    modalEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]') || e.target.hasAttribute('data-modal-overlay')) closeModal();
    });
    return modalEl;
  }
  function closeModal() {
    if (modalEl) { modalEl.remove(); modalEl = null; document.body.style.overflow = ''; }
  }

  function iconSvg(name) {
    const src = $('#icon-defs [data-icon="' + name + '"]');
    return src ? src.innerHTML : '';
  }

  /* ---------- Cart ---------- */

  const cartCountEls = () => $$('[data-cart-count]');

  function updateCartCount(count) {
    cartCountEls().forEach((el) => {
      el.textContent = count;
      el.dataset.count = count;
    });
  }

  async function getCart() {
    return fetchJSON(T.routes.cart + '.js');
  }

  async function addToCart(items) {
    const res = await fetch(T.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add to cart');
    }
    return res.json();
  }

  async function changeLine(line, quantity) {
    return fetchJSON(T.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
  }

  function renderFreeShipBar(cart) {
    const bar = $('[data-free-ship]');
    if (!bar) return;
    const threshold = T.freeShippingThreshold || 0;
    if (!threshold) { bar.hidden = true; return; }
    bar.hidden = false;
    const remaining = threshold - cart.total_price;
    const pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    const msg = bar.querySelector('.msg');
    if (remaining <= 0) {
      msg.innerHTML = '<strong>' + escapeHtml(T.strings.freeShippingUnlocked) + '</strong>';
    } else {
      msg.innerHTML = escapeHtml(T.strings.freeShippingRemaining).replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>');
    }
    bar.querySelector('.fill').style.width = pct + '%';
  }

  function renderCartDrawer(cart) {
    updateCartCount(cart.item_count);
    const body = $('[data-cart-lines]');
    const foot = $('[data-cart-foot]');
    if (!body) return;
    renderFreeShipBar(cart);

    if (!cart.items.length) {
      body.innerHTML =
        '<div class="cart-empty">' + iconSvg('cart') +
        '<p>' + escapeHtml($('[data-cart-empty-text]')?.textContent || 'Your cart is empty') + '</p>' +
        '<a class="btn btn--sm" style="margin-top:18px" href="' + T.routes.root + 'collections/all">' +
        escapeHtml($('[data-cart-empty-cta]')?.textContent || 'Start shopping') + '</a></div>';
      if (foot) foot.hidden = true;
      return;
    }
    if (foot) foot.hidden = false;

    body.innerHTML = cart.items.map((item, i) => {
      const img = item.featured_image
        ? '<img src="' + item.featured_image.url.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2') + '" alt="' + escapeHtml(item.featured_image.alt || item.product_title) + '" loading="lazy">'
        : '<div class="skeleton" style="width:76px;height:95px"></div>';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? '<div class="cart-line__variant">' + escapeHtml(item.variant_title) + '</div>' : '';
      return (
        '<div class="cart-line" data-line="' + (i + 1) + '">' +
        '<a href="' + item.url + '">' + img + '</a>' +
        '<div class="cart-line__info">' +
        '<div class="cart-line__title"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></div>' +
        variantTitle +
        '<div class="cart-line__row">' +
        '<div class="qty-stepper">' +
        '<button type="button" data-qty-minus aria-label="−">' + iconSvg('minus') + '</button>' +
        '<input type="number" value="' + item.quantity + '" min="0" data-qty-input inputmode="numeric">' +
        '<button type="button" data-qty-plus aria-label="+">' + iconSvg('plus') + '</button>' +
        '</div>' +
        '<div style="text-align:right">' +
        '<div class="cart-line__price">' + formatMoney(item.final_line_price) + '</div>' +
        '<button class="cart-line__remove" data-line-remove>Remove</button>' +
        '</div></div></div></div>'
      );
    }).join('');

    const subtotal = $('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  }

  /* Cart upsell: recommend products based on the first line item */
  async function renderCartUpsell(cart) {
    const box = $('[data-cart-upsell]');
    if (!box) return;
    if (!cart.items.length) { box.hidden = true; return; }
    try {
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const data = await fetchJSON(T.routes.root + 'recommendations/products.json?product_id=' + cart.items[0].product_id + '&limit=4&intent=related');
      const recs = (data.products || []).filter((p) => !inCart.has(p.id) && p.available).slice(0, 2);
      if (!recs.length) { box.hidden = true; return; }
      box.hidden = false;
      $('[data-cart-upsell-items]').innerHTML = recs.map((p) => {
        const v = p.variants.find((x) => x.available) || p.variants[0];
        const single = p.variants.length === 1;
        const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_120x$1$2') : '';
        return (
          '<div class="cart-upsell__item">' +
          '<a href="' + p.url + '">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div class="cart-upsell__info">' +
          '<a class="t" href="' + p.url + '">' + escapeHtml(p.title) + '</a>' +
          '<span class="p">' + formatMoney(v.price) + '</span>' +
          '</div>' +
          '<button class="btn btn--ghost btn--sm" data-quick-add data-handle="' + p.handle + '"' +
          (single ? ' data-single-variant="true" data-variant-id="' + v.id + '"' : '') + '>+</button>' +
          '</div>'
        );
      }).join('');
    } catch (e) { box.hidden = true; }
  }

  async function refreshCart() {
    try {
      const cart = await getCart();
      renderCartDrawer(cart);
      renderCartUpsell(cart);
    } catch (e) { /* network hiccup — keep current UI */ }
  }

  function openCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    if (drawer) { drawerOpen(drawer); refreshCart(); }
  }
  window.oryxOpenCart = openCartDrawer;

  document.addEventListener('click', async (e) => {
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle) { e.preventDefault(); openCartDrawer(); return; }

    const line = e.target.closest('.cart-line');
    if (line) {
      const lineNo = parseInt(line.dataset.line, 10);
      const input = line.querySelector('[data-qty-input]');
      if (e.target.closest('[data-qty-minus]')) {
        renderCartDrawer(await changeLine(lineNo, Math.max(0, parseInt(input.value, 10) - 1)));
      } else if (e.target.closest('[data-qty-plus]')) {
        renderCartDrawer(await changeLine(lineNo, parseInt(input.value, 10) + 1));
      } else if (e.target.closest('[data-line-remove]')) {
        renderCartDrawer(await changeLine(lineNo, 0));
      }
    }
  });
  document.addEventListener('change', async (e) => {
    if (e.target.matches('[data-qty-input]') && e.target.closest('.cart-line')) {
      const lineNo = parseInt(e.target.closest('.cart-line').dataset.line, 10);
      renderCartDrawer(await changeLine(lineNo, Math.max(0, parseInt(e.target.value, 10) || 0)));
    }
  });

  /* AJAX product forms */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = T.strings.adding || 'Adding…'; }
    try {
      const fd = new FormData(form);
      await addToCart([{ id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) }]);
      closeModal();
      openCartDrawer();
      toast(T.strings.addedToCart);
    } catch (err) {
      toast(err.message);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = original; }
    }
  });

  /* Quick add: single-variant adds instantly, multi-variant opens quick view */
  document.addEventListener('click', async (e) => {
    const qa = e.target.closest('[data-quick-add]');
    if (!qa) return;
    e.preventDefault();
    const variantId = qa.dataset.variantId;
    const handle = qa.dataset.handle;
    if (variantId && qa.dataset.singleVariant === 'true') {
      qa.disabled = true;
      try {
        await addToCart([{ id: parseInt(variantId, 10), quantity: 1 }]);
        openCartDrawer();
        toast(T.strings.addedToCart);
      } catch (err) { toast(err.message); }
      qa.disabled = false;
    } else if (handle) {
      openQuickView(handle);
    }
  });

  /* ---------- Quick view ---------- */

  async function openQuickView(handle) {
    const m = openModal('<div class="quick-view"><div class="skeleton" style="aspect-ratio:3/4"></div><div class="quick-view__info"><div class="skeleton" style="height:28px;width:70%"></div><div class="skeleton" style="height:20px;width:40%"></div></div></div>');
    try {
      const p = await fetchJSON(T.routes.root + 'products/' + handle + '.js');
      const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_800x$1$2') : '';
      const hasOptions = !(p.variants.length === 1 && p.variants[0].title === 'Default Title');
      // /products/{handle}.js returns options as objects: { name, position, values }
      const optionNames = (p.options || []).map((o) => (typeof o === 'string' ? o : (o && o.name) || ''));
      const optionsHtml = hasOptions
        ? optionNames.map((optName, oi) => {
            const values = [...new Set(p.variants.map((v) => v.options[oi]))];
            return (
              '<div class="option-group"><div class="option-group__label">' + escapeHtml(optName) + '</div>' +
              '<div class="option-pills">' + values.map((val, vi) =>
                '<label class="option-pill"><input type="radio" name="qv-option-' + oi + '" value="' + escapeHtml(val) + '"' + (vi === 0 ? ' checked' : '') + '><span>' + escapeHtml(val) + '</span></label>'
              ).join('') + '</div></div>'
            );
          }).join('')
        : '';
      const panel = m.querySelector('.modal__panel');
      panel.innerHTML =
        '<button class="modal__close" data-modal-close aria-label="Close">' + iconSvg('close') + '</button>' +
        '<div class="quick-view">' +
        '<div class="quick-view__media">' + (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '">' : '') + '</div>' +
        '<div class="quick-view__info">' +
        '<div class="product-info__vendor">' + escapeHtml(p.type || p.vendor) + '</div>' +
        '<h2 style="font-size:1.5rem">' + escapeHtml(p.title) + '</h2>' +
        '<div class="product-info__price" data-qv-price></div>' +
        optionsHtml +
        '<button class="btn btn--full" data-qv-add style="display:flex;align-items:center;justify-content:center;width:100%;margin-top:18px;margin-bottom:12px;padding:15px 24px;border-radius:999px;background:linear-gradient(135deg,#f1d28a,#b98b3f);color:#120d08;font-weight:700;letter-spacing:.08em;border:0;cursor:pointer;">' + escapeHtml(T.strings.addToCart) + '</button>' +
        '<a href="' + p.url + '" style="text-align:center;font-size:0.85rem;color:var(--c-cyan);text-decoration:underline;text-underline-offset:3px">View full details</a>' +
        '</div></div>';

      let current = p.variants.find((v) => v.available) || p.variants[0];
      const priceEl = panel.querySelector('[data-qv-price]');
      const addBtn = panel.querySelector('[data-qv-add]');

      function syncVariant() {
        if (hasOptions) {
          const chosen = p.options.map((_, oi) => panel.querySelector('input[name="qv-option-' + oi + '"]:checked')?.value);
          current = p.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        }
        if (!current) {
          priceEl.textContent = '';
          addBtn.disabled = true;
          addBtn.textContent = T.strings.unavailable;
          return;
        }
        priceEl.innerHTML = current.compare_at_price > current.price
          ? '<span class="price__sale">' + formatMoney(current.price) + '</span><s class="price__compare">' + formatMoney(current.compare_at_price) + '</s>'
          : formatMoney(current.price);
        addBtn.disabled = !current.available;
        addBtn.textContent = current.available ? T.strings.addToCart : T.strings.soldOut;
      }
      if (hasOptions) {
        // preselect the first available variant's options
        current = p.variants.find((v) => v.available) || p.variants[0];
        current.options.forEach((val, oi) => {
          const input = panel.querySelector('input[name="qv-option-' + oi + '"][value="' + CSS.escape(val) + '"]');
          if (input) input.checked = true;
        });
        panel.addEventListener('change', syncVariant);
      }
      syncVariant();
      addBtn.addEventListener('click', async () => {
        addBtn.disabled = true;
        try {
          await addToCart([{ id: current.id, quantity: 1 }]);
          closeModal();
          openCartDrawer();
          toast(T.strings.addedToCart);
        } catch (err) { toast(err.message); addBtn.disabled = false; }
      });
    } catch (err) {
      closeModal();
      toast('Could not load product');
    }
  }
  window.oryxQuickView = openQuickView;

  /* ---------- Predictive search ---------- */

  const searchOverlay = $('[data-search-overlay]');
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      searchOverlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => searchOverlay.querySelector('input[type="search"]')?.focus(), 60);
    }
  });

  if (searchOverlay) {
    const input = searchOverlay.querySelector('input[type="search"]');
    const results = searchOverlay.querySelector('[data-search-results]');
    input.addEventListener('input', debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      try {
        const data = await fetchJSON(
          T.routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last'
        );
        const r = data.resources.results;
        let html = '';
        if (r.products?.length) {
          html += '<div class="search-results__group-title">Products</div>' + r.products.map((p) =>
            '<a class="search-result" href="' + p.url + '">' +
            (p.image ? '<img src="' + p.image.replace(/(\.[a-z]+)(\?|$)/, '_120x$1$2') + '" alt="">' : '') +
            '<div><div class="t">' + escapeHtml(p.title) + '</div><div class="p">' + formatMoney(Math.round(parseFloat(p.price) * 100)) + '</div></div></a>'
          ).join('');
        }
        const others = [...(r.pages || []), ...(r.articles || [])];
        if (others.length) {
          html += '<div class="search-results__group-title">Pages & posts</div>' + others.map((x) =>
            '<a class="search-result" href="' + x.url + '"><div class="t">' + escapeHtml(x.title) + '</div></a>'
          ).join('');
        }
        if (!html) html = '<p style="color:var(--c-text-2);padding:14px">' + escapeHtml($('[data-search-empty-text]')?.textContent || 'Nothing found.') + '</p>';
        else html += '<a class="btn btn--ghost btn--sm" style="margin-top:14px" href="' + T.routes.search + '?q=' + encodeURIComponent(q) + '">View all results</a>';
        results.innerHTML = html;
      } catch (e) { /* ignore */ }
    }, 220));
  }

  /* ---------- Wishlist ---------- */

  const WL_KEY = 'oryx:wishlist';
  const wl = {
    get: () => { try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch { return []; } },
    set(arr) { try { localStorage.setItem(WL_KEY, JSON.stringify(arr)); } catch (e) { /* private mode / quota */ } wl.syncUI(); },
    has: (h) => wl.get().includes(h),
    toggle(h) {
      const arr = wl.get();
      const i = arr.indexOf(h);
      if (i >= 0) { arr.splice(i, 1); wl.set(arr); return false; }
      arr.unshift(h); wl.set(arr.slice(0, 60)); return true;
    },
    syncUI() {
      const list = wl.get();
      $$('[data-wishlist-toggle]').forEach((btn) => {
        btn.classList.toggle('active', list.includes(btn.dataset.wishlistToggle));
      });
      $$('[data-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
        el.dataset.count = list.length;
      });
    }
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const added = wl.toggle(btn.dataset.wishlistToggle);
    toast(added ? T.strings.wishlistAdded : T.strings.wishlistRemoved, 1800);
    // If a wishlist grid is open, reflect the change immediately
    if (!added) {
      const card = btn.closest('.product-card');
      const inGrid = card && card.closest('[data-wishlist-grid]');
      if (inGrid) card.remove();
      $$('[data-wishlist-grid]').forEach((g) => {
        if (!wl.get().length) renderWishlistGrid(g);
      });
    }
  });

  /* ---------- Client-rendered product card (wishlist / recently viewed) ---------- */

  function productCardHTML(p) {
    const img1 = p.images?.[0] ? p.images[0].replace(/(\.[a-z]+)(\?|$)/, '_600x$1$2') : '';
    const onSale = p.compare_at_price > p.price;
    const singleVariant = p.variants.length === 1 && p.variants[0].title === 'Default Title';
    return (
      '<div class="product-card">' +
      '<div class="product-card__media">' +
      (img1 ? '<img class="primary" src="' + img1 + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
      '<div class="product-card__badges">' +
      (!p.available ? '<span class="badge badge--soldout">Sold out</span>' : onSale ? '<span class="badge badge--sale">Sale</span>' : '') +
      '</div>' +
      '<button class="product-card__wishlist' + (wl.has(p.handle) ? ' active' : '') + '" data-wishlist-toggle="' + p.handle + '" aria-label="Wishlist">' + iconSvg('heart') + '</button>' +
      (p.available ? '<button class="product-card__quick" data-quick-add data-handle="' + p.handle + '"' +
        (singleVariant ? ' data-single-variant="true" data-variant-id="' + p.variants[0].id + '"' : '') + '>' +
        (singleVariant ? escapeHtml(T.strings.addToCart) : 'Choose Size') + '</button>' : '') +
      '</div>' +
      '<div class="product-card__info">' +
      '<div class="product-card__type">' + escapeHtml(p.type || '') + '</div>' +
      '<h3 class="product-card__title"><a href="' + p.url + '">' + escapeHtml(p.title) + '</a></h3>' +
      '<div class="product-card__price">' +
      (onSale
        ? '<span class="price__sale">' + formatMoney(p.price) + '</span><s class="price__compare">' + formatMoney(p.compare_at_price) + '</s>'
        : formatMoney(p.price)) +
      '</div></div></div>'
    );
  }

  async function renderHandleGrid(container, handles, emptyMsg) {
    if (!handles.length) {
      container.innerHTML = '<p style="color:var(--c-text-2)">' + escapeHtml(emptyMsg) + '</p>';
      return false;
    }
    container.innerHTML = handles.map(() => '<div class="skeleton" style="aspect-ratio:3/4"></div>').join('');
    const products = (await Promise.all(
      handles.map((h) => fetchJSON(T.routes.root + 'products/' + h + '.js').catch(() => null))
    )).filter(Boolean);
    if (!products.length) {
      container.innerHTML = '<p style="color:var(--c-text-2)">' + escapeHtml(emptyMsg) + '</p>';
      return false;
    }
    container.innerHTML = products.map(productCardHTML).join('');
    wl.syncUI();
    return true;
  }

  /* Wishlist grids (page + drawer) */
  function renderWishlistGrid(grid) {
    renderHandleGrid(grid, wl.get(), grid.dataset.emptyText || 'Nothing saved yet.');
  }

  // Render any wishlist grid already on the page (e.g. the wishlist page template)
  $$('[data-wishlist-grid]').forEach((g) => {
    if (!g.closest('[data-wishlist-drawer]')) renderWishlistGrid(g);
  });

  // Header heart opens the wishlist drawer and refreshes it
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-wishlist-open]')) return;
    e.preventDefault();
    const drawer = $('[data-wishlist-drawer]');
    if (drawer) {
      const grid = $('[data-wishlist-grid]', drawer);
      if (grid) renderWishlistGrid(grid);
      drawerOpen(drawer);
    }
  });

  /* ---------- Recently viewed ---------- */

  const RV_KEY = 'oryx:recent';
  const currentHandle = document.body.dataset.productHandle;
  if (currentHandle) {
    try {
      let arr = JSON.parse(localStorage.getItem(RV_KEY)) || [];
      arr = [currentHandle, ...arr.filter((h) => h !== currentHandle)].slice(0, 12);
      localStorage.setItem(RV_KEY, JSON.stringify(arr));
    } catch { /* private mode */ }
  }
  $$('[data-recently-viewed]').forEach(async (section) => {
    let handles = [];
    try { handles = (JSON.parse(localStorage.getItem(RV_KEY)) || []).filter((h) => h !== currentHandle).slice(0, 4); } catch {}
    const grid = section.querySelector('[data-rv-grid]');
    if (!handles.length) { section.hidden = true; return; }
    const ok = await renderHandleGrid(grid, handles, '');
    section.hidden = !ok;
  });

  /* ---------- Hero card stack: click/tap or keyboard to shuffle ---------- */

  function initHeroStack(stack) {
    if (!stack || stack.dataset.stackInit) return;
    stack.dataset.stackInit = '1';
    const cards = $$('[data-stack-card]', stack);
    if (cards.length < 2) return;
    function shuffle() {
      cards.forEach((card) => {
        const pos = ['is-1', 'is-2', 'is-3'].findIndex((c) => card.classList.contains(c));
        if (pos === -1) return;
        card.classList.remove('is-' + (pos + 1));
        card.classList.add('is-' + ((pos + 2) > 3 ? 1 : pos + 2));
      });
    }
    stack.addEventListener('click', shuffle);
    stack.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); shuffle(); }
    });
  }
  $$('[data-hero-stack]').forEach(initHeroStack);

  /* ---------- Scroll reveal ----------
     Only elements clearly below the fold get armed (hidden) — everything
     else stays exactly as server-rendered. Purely additive enhancement. */

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('in-view'); revealObserver.unobserve(en.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  $$('.reveal, .reveal-stagger').forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.top > window.innerHeight * 0.92) {
      el.classList.add('reveal-armed');
      revealObserver.observe(el);
    }
  });

  /* ---------- Header scroll state ---------- */

  const header = $('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Desktop nav dropdown (click + hover intent) */
  $$('.nav-item[data-has-menu]').forEach((item) => {
    let timer;
    item.addEventListener('mouseenter', () => { clearTimeout(timer); item.classList.add('open'); });
    item.addEventListener('mouseleave', () => { timer = setTimeout(() => item.classList.remove('open'), 120); });
  });

  /* "More" toggle: on desktop it just opens the dropdown (hover/focus via CSS),
     so suppress navigation; on mobile the desktop nav is hidden anyway. */
  document.addEventListener('click', (e) => {
    const noop = e.target.closest('[data-menu-noop]');
    if (noop && matchMedia('(min-width: 990px)').matches) e.preventDefault();
  });

  /* Copy-to-clipboard — works on any [data-copy-code] element (coupons, popup) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy-code]');
    if (!btn) return;
    const code = btn.dataset.copyCode;
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      toast(T.strings.codeCopied || 'Code copied ✓', 1800);
      btn.classList.add('copied');
      setTimeout(() => btn.classList.remove('copied'), 1500);
    }).catch(() => {});
  });

  /* Share current page (article, etc.) */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-share-page]')) return;
    const payload = { title: document.title, url: location.href };
    if (navigator.share) {
      navigator.share(payload).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(location.href).then(() => toast('Link copied ✓', 1800)).catch(() => {});
    }
  });

  /* Mobile menu */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-menu-toggle]')) {
      e.preventDefault();
      const d = $('[data-mobile-menu]');
      if (d) drawerOpen(d);
    }
  });

  /* ---------- Countdown ---------- */

  $$('[data-countdown]').forEach((el) => {
    const end = new Date(el.dataset.countdown).getTime();
    if (isNaN(end)) return;
    const cells = {
      d: el.querySelector('[data-cd-days]'),
      h: el.querySelector('[data-cd-hours]'),
      m: el.querySelector('[data-cd-mins]'),
      s: el.querySelector('[data-cd-secs]')
    };
    const pad = (n) => String(n).padStart(2, '0');
    function tick() {
      const diff = end - Date.now();
      if (diff <= 0) {
        el.classList.add('ended');
        const endedMsg = el.dataset.endedText;
        if (endedMsg) el.innerHTML = '<p style="color:var(--c-text-2);font-size:1.1rem">' + escapeHtml(endedMsg) + '</p>';
        clearInterval(iv);
        return;
      }
      cells.d.textContent = pad(Math.floor(diff / 864e5));
      cells.h.textContent = pad(Math.floor(diff / 36e5) % 24);
      cells.m.textContent = pad(Math.floor(diff / 6e4) % 60);
      cells.s.textContent = pad(Math.floor(diff / 1e3) % 60);
    }
    tick();
    const iv = setInterval(tick, 1000);
  });

  /* ---------- Hero B: 3D drop carousel ---------- */

  function initCarousel(car) {
    if (car.dataset.carInit) return;
    car.dataset.carInit = '1';
    const slides = $$('[data-hcar-slide]', car);
    const captions = $$('[data-hcar-caption]', car);
    const dots = $$('[data-hcar-dot]', car);
    const n = slides.length;
    if (!n) return;
    let active = 0;
    let timer = null;

    function paint() {
      slides.forEach((s) => {
        const i = parseInt(s.dataset.index, 10);
        s.classList.remove('pos-center', 'pos-left', 'pos-right');
        if (i === active) s.classList.add('pos-center');
        else if (n > 1 && i === (active - 1 + n) % n) s.classList.add('pos-left');
        else if (n > 1 && i === (active + 1) % n) s.classList.add('pos-right');
      });
      captions.forEach((c) => c.classList.toggle('active', parseInt(c.dataset.index, 10) === active));
      dots.forEach((d) => d.classList.toggle('active', parseInt(d.dataset.index, 10) === active));
    }

    function go(i, user) {
      active = ((i % n) + n) % n;
      paint();
      if (user) restart();
    }

    function restart() {
      clearInterval(timer);
      const ms = parseInt(car.dataset.autoplay, 10) || 0;
      if (ms > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = setInterval(() => { if (!document.hidden && !car.matches(':hover')) go(active + 1); }, ms);
      }
    }

    car.addEventListener('click', (e) => {
      if (e.target.closest('[data-hcar-next]')) go(active + 1, true);
      else if (e.target.closest('[data-hcar-prev]')) go(active - 1, true);
      else {
        const dot = e.target.closest('[data-hcar-dot]');
        if (dot) go(parseInt(dot.dataset.index, 10), true);
        else {
          const slide = e.target.closest('[data-hcar-slide]');
          if (slide && !slide.classList.contains('pos-center')) go(parseInt(slide.dataset.index, 10), true);
        }
      }
    });

    const stage = $('.hcar__stage', car);
    if (stage) {
      stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1, true); }
      });
      let startX = null;
      stage.addEventListener('pointerdown', (e) => { startX = e.clientX; }, { passive: true });
      stage.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) go(active + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
    }

    // Theme editor: selecting a slide block jumps to it
    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target && e.target.closest && e.target.closest('[data-hcar-slide]');
      if (slide && car.contains(slide)) go(parseInt(slide.dataset.index, 10), true);
    });

    paint();
    restart();
  }
  $$('[data-hero-carousel]').forEach(initCarousel);

  /* ---------- Hero D: manga panel expand on hover/tap ---------- */

  function initMangaGrid(grid) {
    if (grid.dataset.mangaInit) return;
    grid.dataset.mangaInit = '1';
    const panels = $$('[data-manga-panel]', grid);
    if (!panels.length) return;

    function activate(panel) {
      panels.forEach((p) => p.classList.toggle('active', p === panel));
    }
    panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => activate(panel));
      // Touch: first tap expands, second tap (on the active one) follows the link
      panel.addEventListener('click', (e) => {
        if (matchMedia('(hover: none)').matches && !panel.classList.contains('active')) {
          e.preventDefault();
          activate(panel);
        }
      });
      panel.addEventListener('focus', () => activate(panel));
    });
  }
  $$('[data-manga-grid]').forEach(initMangaGrid);

  /* ---------- Size guide (product page) ---------- */

  function initSizeGuide(el) {
    if (el.dataset.sgInit) return;
    el.dataset.sgInit = '1';
    const modal = $('[data-sg-modal]', el);
    const openBtn = $('[data-sg-open]', el);
    if (!modal || !openBtn) return;

    // Relocate the trigger next to the quantity stepper (same product form)
    const scope = el.closest('form') || el.closest('.product-info') || document;
    const qtyRow = $('[data-qty-row]', scope);
    if (qtyRow && !qtyRow.contains(openBtn)) qtyRow.appendChild(openBtn);

    const supportsDialog = typeof modal.showModal === 'function';
    function open() { if (supportsDialog) modal.showModal(); else { modal.setAttribute('open', ''); modal.style.cssText = 'position:fixed;inset:0;z-index:300;margin:auto'; } }
    function close() { if (supportsDialog) modal.close(); else modal.removeAttribute('open'); }

    openBtn.addEventListener('click', open);

    // Tee visualizer state
    const tee = $('[data-sg-tee]', el);
    const teeBadge = $('[data-sg-tee-size]', el);
    const vizChest = $('[data-sg-viz-chest]', el);
    const vizLength = $('[data-sg-viz-length]', el);
    const rows = $$('[data-sg-row]', el);
    let unit = 'cm';
    const chests = rows.map((r) => parseFloat(r.dataset.chest)).filter((n) => !isNaN(n));
    const minChest = chests.length ? Math.min.apply(null, chests) : 0;

    function fmt(cm) { return unit === 'in' ? (cm / 2.54).toFixed(1) + ' in' : cm + ' cm'; }

    function selectRow(row) {
      rows.forEach((r) => r.classList.remove('is-rec'));
      row.classList.add('is-rec');
      const chest = parseFloat(row.dataset.chest);
      const length = parseFloat(row.dataset.length);
      if (teeBadge) teeBadge.textContent = row.dataset.size || '';
      if (vizChest) vizChest.textContent = isNaN(chest) ? '—' : fmt(chest);
      if (vizLength) vizLength.textContent = isNaN(length) ? '—' : fmt(length);
      if (tee && minChest) tee.style.setProperty('--tee-scale', (0.9 + (chest - minChest) / 60).toFixed(3));
    }

    rows.forEach((row) => {
      row.style.cursor = 'pointer';
      row.addEventListener('click', () => selectRow(row));
      row.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectRow(row); } });
    });

    // Enter in the finder input triggers recommend, never the product form's add-to-cart
    const chestInput = $('[data-sg-chest]', el);
    if (chestInput) chestInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); const b = $('[data-sg-find]', el); if (b) b.click(); }
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.closest('[data-sg-close]')) { close(); return; }
      const unitBtn = e.target.closest('[data-sg-unit]');
      if (unitBtn) {
        unit = unitBtn.dataset.sgUnit;
        $$('[data-sg-unit]', el).forEach((b) => b.classList.toggle('is-active', b === unitBtn));
        $$('[data-cm]', el).forEach((td) => {
          const cm = parseFloat(td.dataset.cm);
          if (!isNaN(cm)) td.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : cm;
        });
        $$('[data-m]', el).forEach((th) => { th.textContent = (th.dataset.label || '') + (unit === 'in' ? ' (in)' : ' (cm)'); });
        const active = $('[data-sg-row].is-rec', el);
        if (active) selectRow(active);
        return;
      }
      if (e.target.closest('[data-sg-find]')) {
        const input = $('[data-sg-chest]', el);
        const res = $('[data-sg-result]', el);
        const val = parseFloat(input.value);
        if (isNaN(val) || val < 50 || val > 200) { res.hidden = false; res.textContent = 'Enter a chest measurement in cm (e.g. 98).'; return; }
        let pick = null;
        for (const r of rows) { if (parseFloat(r.dataset.chest) >= val + 4) { pick = r; break; } }
        if (!pick) pick = rows[rows.length - 1];
        if (!pick) return;
        selectRow(pick);
        res.hidden = false;
        res.innerHTML = 'We recommend <b>' + escapeHtml(pick.dataset.size || pick.cells[0].textContent) + '</b> for a relaxed fit. Want it tighter? Size down.';
      }
    });
  }
  $$('[data-sizeguide]').forEach(initSizeGuide);

  /* ---------- Behind the scenes: 3D tilt + lightbox ---------- */

  function initBtsGrid(grid) {
    if (grid.dataset.btsInit) return;
    grid.dataset.btsInit = '1';
    const cards = $$('[data-bts-card]', grid);
    const canTilt = grid.dataset.tilt === 'true' && matchMedia('(hover: hover) and (prefers-reduced-motion: no-preference)').matches;
    const canLightbox = grid.dataset.lightbox === 'true';

    cards.forEach((card) => {
      if (canTilt) {
        let raf = null;
        card.addEventListener('pointermove', (e) => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            const r = card.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
            card.style.setProperty('--ry', ((px - 0.5) * 12).toFixed(2) + 'deg');
            card.style.setProperty('--rx', ((0.5 - py) * 12).toFixed(2) + 'deg');
            const glare = card.querySelector('.bts-card__glare');
            if (glare) { glare.style.setProperty('--gx', (px * 100).toFixed(1) + '%'); glare.style.setProperty('--gy', (py * 100).toFixed(1) + '%'); }
            raf = null;
          });
        });
        card.addEventListener('pointerleave', () => {
          card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg');
        });
      }
      if (canLightbox) {
        card.addEventListener('click', () => openBtsLightbox(cards, card));
        card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBtsLightbox(cards, card); } });
      }
    });
  }

  function openBtsLightbox(cards, startCard) {
    const list = cards.filter((c) => c.dataset.full);
    let idx = list.indexOf(startCard);
    if (idx < 0) return;
    const lb = document.createElement('div');
    lb.className = 'bts-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.innerHTML =
      '<button class="bts-lightbox__btn bts-lightbox__close" aria-label="Close">✕</button>' +
      (list.length > 1 ? '<button class="bts-lightbox__btn bts-lightbox__prev" aria-label="Previous">←</button><button class="bts-lightbox__btn bts-lightbox__next" aria-label="Next">→</button>' : '') +
      '<img alt=""><div class="bts-lightbox__cap"></div>';
    document.body.appendChild(lb);
    document.body.style.overflow = 'hidden';
    const imgEl = lb.querySelector('img'), capEl = lb.querySelector('.bts-lightbox__cap');
    function show(i) { idx = (i + list.length) % list.length; imgEl.src = list[idx].dataset.full; capEl.textContent = list[idx].dataset.cap || ''; }
    function close() { lb.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); if (e.key === 'ArrowRight') show(idx + 1); if (e.key === 'ArrowLeft') show(idx - 1); }
    lb.addEventListener('click', (e) => {
      if (e.target.closest('.bts-lightbox__close') || e.target === lb) close();
      else if (e.target.closest('.bts-lightbox__next')) show(idx + 1);
      else if (e.target.closest('.bts-lightbox__prev')) show(idx - 1);
    });
    document.addEventListener('keydown', onKey);
    show(idx);
  }
  $$('[data-bts-grid]').forEach(initBtsGrid);

  /* ---------- Track your order: build tracking URL ---------- */

  function initTrackRoot(root) {
    if (root.dataset.trackInit) return;
    root.dataset.trackInit = '1';
    root.addEventListener('click', (e) => {
      const go = e.target.closest('[data-track-go]');
      if (!go || go.tagName === 'A') return;
      const card = go.closest('.track-carrier');
      const input = card.querySelector('[data-track-input]');
      const err = card.querySelector('[data-track-err]');
      const num = (input.value || '').trim();
      if (!num) { if (err) err.hidden = false; input.focus(); return; }
      if (err) err.hidden = true;
      const url = go.dataset.pattern.replace('{number}', encodeURIComponent(num));
      window.open(url, '_blank', 'noopener');
    });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-track-input]')) {
        e.preventDefault();
        const go = e.target.closest('.track-carrier').querySelector('[data-track-go]');
        if (go && go.tagName !== 'A') go.click();
      }
    });
  }
  $$('[data-track-root]').forEach(initTrackRoot);

  /* ---------- Returns: build a pre-filled email ---------- */

  function initReturnRoot(root) {
    if (root.dataset.returnInit) return;
    root.dataset.returnInit = '1';
    root.addEventListener('click', (e) => {
      const go = e.target.closest('[data-return-go]');
      if (!go) return;
      const order = (root.querySelector('[data-return-order]').value || '').trim();
      const reasonEl = root.querySelector('[data-return-reason]');
      const reason = reasonEl ? reasonEl.value : '';
      const email = go.dataset.email || '';
      const subject = 'Return / Exchange Request' + (order ? ' - Order ' + order : '');
      const body = 'Order number: ' + order + '\nReason: ' + reason + '\n\nDetails (please describe the issue, size wanted, etc.):\n';
      window.location.href = 'mailto:' + email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    });
  }
  $$('[data-return-root]').forEach(initReturnRoot);

  /* ---------- Count-up stats (About) ---------- */

  function initCountUp(group) {
    if (group.dataset.countInit) return;
    group.dataset.countInit = '1';
    const els = $$('[data-countup]', group);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        const el = en.target;
        const target = parseInt((el.dataset.countup || '').replace(/[^0-9]/g, ''), 10);
        if (isNaN(target)) return;
        const prefix = el.dataset.prefix || '', suffix = el.dataset.suffix || '';
        const dur = 1200, start = performance.now();
        function tick(now) {
          const p = Math.min(1, (now - start) / dur);
          const val = Math.round(target * (1 - Math.pow(1 - p, 3)));
          el.textContent = prefix + val.toLocaleString('en-IN') + suffix;
          if (p < 1) requestAnimationFrame(tick);
        }
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = prefix + target.toLocaleString('en-IN') + suffix; }
        else requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
  }
  $$('[data-countup-group]').forEach(initCountUp);

  /* ---------- Collection filters: close open dropdowns on outside click ---------- */

  document.addEventListener('click', (e) => {
    if (e.target.closest('.filter-group')) return;
    $$('.filter-group[open]').forEach((d) => d.removeAttribute('open'));
  });

  /* ---------- Collection sort (delegated, replaces inline onchange) ---------- */

  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-sort-select]')) return;
    const u = new URL(location.href);
    u.searchParams.set('sort_by', e.target.value);
    u.searchParams.delete('page');
    location.href = u.href;
  });

  /* ---------- Page loader: fade out when the page is ready ---------- */

  const loader = $('[data-page-loader]');
  if (loader) {
    const hide = () => {
      loader.classList.add('done');
      setTimeout(() => loader.remove(), 600);
    };
    if (document.readyState === 'complete') hide();
    else {
      window.addEventListener('load', hide);
      setTimeout(hide, 4000); // never trap the user behind a stuck loader
    }
  }

  /* ---------- 3D slideshow ---------- */

  function initSlideshow3d(root) {
    if (root.dataset.ssInit) return;
    root.dataset.ssInit = '1';
    const slides = $$('[data-ss-slide]', root);
    const dots = $$('[data-ss-dot]', root);
    const n = slides.length;
    if (n < 2) return;
    let active = 0, timer = null, busy = false;

    function go(next, dir, user) {
      next = ((next % n) + n) % n;
      if (busy || next === active) return;
      busy = true;
      const out = slides[active], inn = slides[next];
      out.classList.add(dir === 1 ? 'is-leaving-left' : 'is-leaving-right');
      inn.classList.add(dir === 1 ? 'is-entering-right' : 'is-entering-left', 'is-active');
      inn.hidden = false;
      setTimeout(() => {
        out.classList.remove('is-active', 'is-leaving-left', 'is-leaving-right');
        out.hidden = true;
        inn.classList.remove('is-entering-right', 'is-entering-left');
        active = next;
        dots.forEach((d) => d.classList.toggle('active', parseInt(d.dataset.index, 10) === active));
        busy = false;
      }, 700);
      if (user) restart();
    }

    function restart() {
      clearInterval(timer);
      const ms = parseInt(root.dataset.autoplay, 10) || 0;
      if (ms > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = setInterval(() => { if (!document.hidden && !root.matches(':hover')) go(active + 1, 1); }, ms);
      }
    }

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-ss-next]')) go(active + 1, 1, true);
      else if (e.target.closest('[data-ss-prev]')) go(active - 1, -1, true);
      else {
        const dot = e.target.closest('[data-ss-dot]');
        if (dot) { const i = parseInt(dot.dataset.index, 10); go(i, i > active ? 1 : -1, true); }
      }
    });

    let sx = null;
    root.addEventListener('pointerdown', (e) => { sx = e.clientX; }, { passive: true });
    root.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx; sx = null;
      if (Math.abs(dx) > 44) go(active + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1, true);
    }, { passive: true });

    document.addEventListener('shopify:block:select', (e) => {
      const s = e.target && e.target.closest && e.target.closest('[data-ss-slide]');
      if (s && root.contains(s)) { const i = parseInt(s.dataset.index, 10); go(i, i > active ? 1 : -1, true); }
    });

    restart();
  }
  $$('[data-slideshow-3d]').forEach(initSlideshow3d);

  /* ---------- Announcement rotation ---------- */

  $$('[data-announcement-rotator]').forEach((bar) => {
    const msgs = $$('.announcement-bar__msg', bar);
    if (msgs.length < 2) return;
    let idx = 0;
    setInterval(() => {
      msgs[idx].hidden = true;
      idx = (idx + 1) % msgs.length;
      msgs[idx].hidden = false;
    }, parseInt(bar.dataset.interval, 10) || 4000);
  });

  /* ---------- Back to top ---------- */

  const btt = $('[data-back-to-top]');
  if (btt) {
    window.addEventListener('scroll', () => {
      btt.classList.toggle('show', window.scrollY > 900);
    }, { passive: true });
    btt.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ---------- Promo popup ---------- */

  const popup = $('[data-promo-popup]');
  if (popup) {
    const KEY = 'oryx:popup-at';
    const freqMs = (parseInt(popup.dataset.frequency, 10) || 7) * 864e5;
    let last = 0;
    try { last = parseInt(localStorage.getItem(KEY), 10) || 0; } catch {}
    const inEditor = window.Shopify && window.Shopify.designMode;
    if (inEditor || Date.now() - last > freqMs) {
      setTimeout(() => popup.classList.add('open'), parseInt(popup.dataset.delay, 10) || 6000);
    }
    function dismissPopup() {
      popup.classList.remove('open');
      try { localStorage.setItem(KEY, String(Date.now())); } catch {}
    }

    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-popup-close]')) dismissPopup();
    });

    // Email-gated reveal: submit to Shopify via AJAX, then show the code step.
    const popupForm = $('[data-popup-form]', popup);
    if (popupForm) {
      popupForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = $('[data-popup-email]', popup);
        const errEl = $('[data-popup-error]', popup);
        const submitBtn = $('[data-popup-submit]', popup);
        const submitLabel = submitBtn ? submitBtn.textContent : '';
        if (email && !email.checkValidity()) { email.reportValidity(); return; }
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '…'; }
        if (errEl) errEl.hidden = true;
        try {
          // Post the real Shopify customer form so the email lands in the list.
          await fetch(popupForm.action, {
            method: 'POST',
            body: new FormData(popupForm),
            headers: { 'Accept': 'text/html' }
          });
          // Shopify returns 200 even on soft errors; we optimistically reveal.
          const emailStep = popup.querySelector('[data-popup-step="email"]');
          const codeStep = popup.querySelector('[data-popup-step="code"]');
          if (emailStep) emailStep.hidden = true;
          if (codeStep) codeStep.hidden = false;
          try { localStorage.setItem(KEY, String(Date.now())); } catch {}
        } catch (err) {
          if (errEl) { errEl.textContent = 'Something went wrong — please try again.'; errEl.hidden = false; }
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
        }
      });
    }
  }

  /* ---------- Newsletter feedback ---------- */

  if (location.search.includes('customer_posted=true')) {
    const nl = $('.newsletter');
    if (nl) nl.scrollIntoView({ block: 'center' });
  }

  /* ---------- Theme editor support ---------- */

  document.addEventListener('shopify:section:load', (e) => {
    $$('.reveal, .reveal-stagger', e.target).forEach((el) => el.classList.remove('reveal-armed'));
    $$('[data-hero-carousel]', e.target).forEach(initCarousel);
    $$('[data-manga-grid]', e.target).forEach(initMangaGrid);
    $$('[data-hero-stack]', e.target).forEach(initHeroStack);
    $$('[data-bts-grid]', e.target).forEach(initBtsGrid);
    $$('[data-track-root]', e.target).forEach(initTrackRoot);
    $$('[data-return-root]', e.target).forEach(initReturnRoot);
    $$('[data-countup-group]', e.target).forEach(initCountUp);
    $$('[data-sizeguide]', e.target).forEach(initSizeGuide);
    $$('[data-slideshow-3d]', e.target).forEach(initSlideshow3d);
  });

  wl.syncUI();
})();
